import { useDebounce } from "@/hooks/useDebounce";
import { AttendeeListItem } from "@/models/prepareRecording";
import { Color, FieldVariant, Loader, LoaderSize, MediumTextS, Space, TableHead, TableRowModel, VerticalFlex } from "@enxcs/dap-ui-stratos";
import { Dispatch, MouseEvent, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingContainer, StyledSearch, TableBodyContainer, TableContentContainer } from "./Attendees.styled";
import { createAttendeesTableHeadings } from "./headers";
import { createAttendeesTableRows, filterAttendeeItems } from "./rows";

export interface AttendeesStepProps {
  attendeesList: AttendeeListItem[];
  isLoadingAttendees: boolean;
  selectedAttendees: AttendeeListItem[];
  onChangeSelectedAttendees: Dispatch<SetStateAction<AttendeeListItem[]>>;
  onToggleAttendee: (attendeeId: string) => void;
}

export const AttendeesStep = ({ attendeesList, isLoadingAttendees, selectedAttendees, onChangeSelectedAttendees, onToggleAttendee }: AttendeesStepProps) => {
  const [filterKey, setFilterKey] = useState("");
  const debouncedFilterKey = useDebounce(filterKey, 1000);

  const { t } = useTranslation();

  const selectedAttendeeIds = useMemo(() => selectedAttendees.map((attendee) => attendee.id), [selectedAttendees]);

  const tableHeadings = useMemo(() => createAttendeesTableHeadings(t), [t]);

  const filteredAttendees = useMemo(() => {
    return filterAttendeeItems(attendeesList, debouncedFilterKey);
  }, [attendeesList, debouncedFilterKey]);

  const tableData: TableRowModel[] = useMemo(() => {
    return createAttendeesTableRows(filteredAttendees, selectedAttendeeIds);
  }, [filteredAttendees, selectedAttendeeIds]);

  const isAllSelected = filteredAttendees.length > 0 && filteredAttendees.every((attendee) => selectedAttendeeIds.includes(attendee.id));

  const handleFilterChange = (value: string) => {
    setFilterKey(value);
    onChangeSelectedAttendees([]);
  };

  const handleSelectAll = (value: boolean) => {
    onChangeSelectedAttendees((currentSelectedAttendees) => {
      if (value) {
        const currentIds = new Set(currentSelectedAttendees.map((attendee) => attendee.id));

        return [...currentSelectedAttendees, ...filteredAttendees.filter((attendee) => !currentIds.has(attendee.id))];
      }

      const filteredIds = new Set(filteredAttendees.map((attendee) => attendee.id));

      return currentSelectedAttendees.filter((attendee) => !filteredIds.has(attendee.id));
    });
  };

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isLoadingAttendees) {
      return;
    }

    const rowElement = (event.target as HTMLElement).closest('[role="row"]');
    const rowGroupElement = rowElement?.parentElement;

    if (!rowElement || !rowGroupElement) {
      return;
    }

    // The checkbox lives in the row's first cell and already toggles selection on its own click
    // (via onSelectRow below) - skip here so a direct checkbox click doesn't double-toggle.
    const clickedCell = (event.target as HTMLElement).closest('[role="cell"]');
    const checkboxCell = rowElement.querySelector('[role="cell"]');
    if (clickedCell === checkboxCell) {
      return;
    }

    const rowElements = Array.from(rowGroupElement.querySelectorAll('[role="row"]'));
    const rowIndex = rowElements.indexOf(rowElement);
    const row = rowIndex >= 0 ? tableData[rowIndex] : undefined;

    if (row) {
      onToggleAttendee(row.key);
    }
  };

  return (
    <VerticalFlex $gap={Space.V8}>
      <StyledSearch
        value={filterKey}
        placeholder={t("buttons.search")}
        clearTitle={t("buttons.clear")}
        role="search"
        id="attendees-search"
        onChange={handleFilterChange}
        variant={FieldVariant.COMPACT}
      />
      <TableContentContainer role="table" onClick={handleRowClick}>
        <TableHead
          headings={tableHeadings}
          selectedAll={isLoadingAttendees ? undefined : isAllSelected}
          selectAllDisabled={isLoadingAttendees || filteredAttendees.length === 0}
          onSelectAll={handleSelectAll}
        />

        <TableBodyContainer
          isLoading={isLoadingAttendees}
          className="border-none"
          selectedKeys={isLoadingAttendees ? undefined : selectedAttendeeIds}
          onSelectRow={(attendeeId) => {
            onToggleAttendee(attendeeId);
          }}
          rows={
            !isLoadingAttendees
              ? tableData
              : [
                  {
                    key: "loadingAttendees",
                    cells: [
                      {
                        content: (
                          <LoadingContainer>
                            <Loader size={LoaderSize.V32} />
                          </LoadingContainer>
                        ),
                      },
                    ],
                  },
                ]
          }
        />
      </TableContentContainer>

      <MediumTextS $tone={Color.Neutral.V50}>{t("prepareModal.guestsStep.totalSelected", { count: selectedAttendeeIds.length })}</MediumTextS>
    </VerticalFlex>
  );
};
